/**
 * DISCUSS WITH SCOUT — ONE ACTION, ONE CONVERSATION, AND NO TEXT IN BETWEEN.
 *
 * The product has exactly one conversation and every structured view is beside
 * it, so "discuss this record" cannot mean opening a second thread. It means
 * taking the record TO the conversation — and the whole design question is what
 * travels.
 *
 * WHAT TRAVELS IS A REFERENCE, NEVER AN EXCERPT. A copied excerpt is FROZEN: the
 * moment the record moves, the copy becomes a silent lie about a record the
 * Owner believes they are still discussing, and neither party can see it happen.
 * So the action carries `(kind, id)` and the revision the Owner was looking at,
 * all four of which the SERVER re-reads and re-resolves under the Owner's own
 * authorization when it answers. If the record changed in between, the answer
 * says so rather than quoting the old one.
 *
 * A LINK AND NOT A BUTTON, and the difference is the reload. The action is a
 * real `<a href="/chat/discuss/<ref>">`, so it can be bookmarked, opened in a new
 * tab, and survives a refresh on the chat side; the click handler only spares the
 * page a full load. A `button` that set a signal would lose the record the
 * instant anything reloaded, and the Owner would be left typing a question about
 * something the page had forgotten.
 *
 * NOTHING HERE CAN START RESEARCH. The reference is data about a record; a turn
 * that carries one is a conversation. The run grammar reads the OWNER's own
 * sentence and refuses anything that arrived inside material — and a reference
 * carries no sentence at all. That is asserted rather than assumed: a specimen
 * drives a turn referencing a record whose stored text contains an imperative,
 * and requires that no window opens.
 */

import { discussPath } from '../chat';
import { navigate } from '../router';

export function DiscussLink({
	ref: reference,
	label = 'Discuss with Scout',
	class: className = 'button ghost small',
}: {
	/** The canonical reference, exactly as the record's own citation spells it —
	 *  `ep:…`, `hyp:…`, `exp-…`, `run:…:report`. */
	ref: string;
	label?: string;
	class?: string;
}) {
	return (
		<a
			href={discussPath(reference)}
			class={className}
			title={`Open the Main Chat with ${reference} attached`}
			onClick={(event: MouseEvent) => {
				// A MODIFIED CLICK IS THE BROWSER'S. Middle-click and "open in a new
				// tab" are the reasons this is a link rather than a button.
				if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
					return;
				}
				event.preventDefault();
				navigate({ destination: 'chat', section: 'discuss', recordId: reference });
			}}
		>
			{label}
		</a>
	);
}
